import {
    Calendar,
    CircleAlert,
    ListChecks,
    PhoneOutgoing,
    Search,
    SlidersHorizontal,
    Users,
} from 'lucide-react';
import { createElement, type ReactNode } from 'react';

import { type HarnessScope } from '@apps/web/features/phoebe_chat_v3/model/event_types';
import { type InterruptResponse } from '@apps/web/features/phoebe_chat_v3/model/interaction_types';
import { type ForegroundClock } from '@apps/web/features/phoebe_chat_v3/model/transcript_store';
import {
    type AgentProfile,
    type AgentProfilePorts,
    type AgentProfileResolver,
    type AgentRendererRegistry,
    type ComposerPreset,
} from '@apps/web/features/phoebe_chat_v3/profiles/agent_profile';
import { type RendererOverlay } from '@apps/web/features/phoebe_chat_v3/registry/types';
import { ChatHomeStage } from '@apps/web/features/phoebe_chat_v3/ui/chat_home';

export type V3PhoebeRendererOverlay = RendererOverlay<AgentRendererRegistry>;

export const V3_PHOEBE_PROFILE_ID = 'v3-phoebe-general';
export const V3_PHOEBE_OUTREACH_PROFILE_ID = 'v3-phoebe-outreach';
export const V3_PHOEBE_RENDERER_VERSION = 'v1';
export const V3_PHOEBE_COPY = {
    emptyTitle: 'How can I help today?',
    emptyBody: 'Ask Phoebe about shifts, callouts, or caregivers.',
    unavailableBody: 'This conversation is not available.',
} as const;
export const V3_PHOEBE_PRESETS: ReadonlyArray<ComposerPreset> = [
    {
        id: 'schedule-today',
        label: 'What’s on the schedule today?',
        icon: Calendar,
    },
    { id: 'find-open-shifts', label: 'Find open shifts', icon: CircleAlert },
    { id: 'find-a-caregiver', label: 'Find a caregiver', icon: Users },
    {
        id: 'start-an-outreach',
        label: 'Start an outreach',
        icon: PhoneOutgoing,
    },
];
export const V3_PHOEBE_OUTREACH_COPY = {
    emptyTitle: 'How can I help with this outreach?',
    emptyBody: 'Ask Phoebe about the shifts or caregivers in this outreach.',
    unavailableBody: 'This conversation is not available.',
} as const;
export const V3_PHOEBE_OUTREACH_PRESETS: ReadonlyArray<ComposerPreset> = [
    {
        id: 'find-more-caregivers',
        label: 'Find more caregivers',
        icon: Search,
    },
    { id: 'explain-this-list', label: 'Explain this list', icon: ListChecks },
    {
        id: 'broaden-the-search',
        label: 'Broaden the search',
        icon: SlidersHorizontal,
    },
];
// On a coordinator-picked outreach the runner is bound to the chat but holds
// no work until it is asked for something, so the empty state says what to
// ask for rather than offering open-ended help.
export const V3_PHOEBE_MANUAL_OUTREACH_COPY = {
    emptyTitle: 'Ask Phoebe to help pick or start.',
    emptyBody: V3_PHOEBE_OUTREACH_COPY.emptyBody,
    unavailableBody: V3_PHOEBE_OUTREACH_COPY.unavailableBody,
} as const;
const V3_PHOEBE_RENDERER_OVERLAY = {} satisfies V3PhoebeRendererOverlay;
const INTERRUPT_DEADLINE_MS = 30_000;

export type V3PhoebeSurface =
    | 'general'
    | 'outreach'
    | 'manual_outreach'
    | 'automation_run';

export function getV3PhoebePresentation(surface: V3PhoebeSurface) {
    if (surface === 'general') {
        return {
            copy: V3_PHOEBE_COPY,
            placeholder: 'Message Phoebe',
            presets: V3_PHOEBE_PRESETS,
            profileId: V3_PHOEBE_PROFILE_ID,
        };
    }
    if (surface === 'automation_run') {
        return {
            copy: V3_PHOEBE_COPY,
            placeholder: 'Ask about this run…',
            presets: V3_PHOEBE_PRESETS,
            profileId: V3_PHOEBE_PROFILE_ID,
        };
    }
    return {
        copy:
            surface === 'manual_outreach'
                ? V3_PHOEBE_MANUAL_OUTREACH_COPY
                : V3_PHOEBE_OUTREACH_COPY,
        placeholder: 'Ask about this outreach…',
        presets: V3_PHOEBE_OUTREACH_PRESETS,
        profileId: V3_PHOEBE_OUTREACH_PROFILE_ID,
    };
}

export type InterruptUiState =
    | Readonly<{ readonly kind: 'idle' }>
    | Readonly<{
          readonly kind: 'pending';
          readonly startedAtForegroundMs: number;
      }>
    | Readonly<{
          readonly kind: 'accepted';
          readonly startedAtForegroundMs: number;
      }>
    | Readonly<{ readonly kind: 'definitively_rejected' }>
    | Readonly<{ readonly kind: 'ambiguous' }>
    | Readonly<{ readonly kind: 'already_inactive' }>
    | Readonly<{ readonly kind: 'authoritatively_resolved' }>;

export type InterruptController = Readonly<{
    readonly getSnapshot: () => InterruptUiState;
    readonly subscribe: (listener: () => void) => () => void;
    readonly requestStop: () => void;
    readonly evaluateDeadline: (authoritativeCatchUpComplete: boolean) => void;
    readonly resolveAuthoritatively: () => void;
    readonly reset: () => void;
    readonly deadlineRemainingMs: () => number | null;
}>;

type InterruptControllerOptions = Readonly<{
    readonly foregroundClock: ForegroundClock;
    readonly interrupt: (() => Promise<InterruptResponse>) | null;
    /** Resume acknowledgement waiting after a harness remount, without posting. */
    readonly resumePendingStop?: boolean;
    /** Release the local in-flight guard when acknowledgement waiting expires. */
    readonly onDeadline?: () => void;
}>;

/**
 * Owns interrupt presentation for the current response. Only an explicit
 * `requestStop` invokes the port; polling never retries a mutation.
 */
export function createInterruptController(
    options: InterruptControllerOptions,
): InterruptController {
    let state: InterruptUiState = options.resumePendingStop
        ? {
              kind: 'pending',
              startedAtForegroundMs: options.foregroundClock.elapsedMs(),
          }
        : { kind: 'idle' };
    // Bumped by every request and by `reset`, so a result that lands after
    // the run moved on cannot describe a newer response.
    let requestGeneration = 0;
    const listeners = new Set<() => void>();

    function publish(nextState: InterruptUiState): void {
        state = nextState;
        for (const listener of Array.from(listeners)) {
            listener();
        }
    }

    function publishRequestResult(
        generation: number,
        startedAtForegroundMs: number,
        result: InterruptResponse,
    ): void {
        if (
            generation !== requestGeneration ||
            state.kind === 'authoritatively_resolved'
        ) {
            return;
        }
        switch (result.kind) {
            case 'accepted':
                // The server took the request but had nothing to stop: the
                // run was already inactive, so no stop is in flight and the
                // local stop row must not claim one. Clear Stop immediately,
                // even if polling still carries stale liveness evidence.
                // An enqueued stop keeps its deadline until polling confirms it.
                publish(
                    result.interrupted
                        ? { kind: 'accepted', startedAtForegroundMs }
                        : { kind: 'already_inactive' },
                );
                return;
            case 'definitively_rejected':
                publish({ kind: 'definitively_rejected' });
                return;
            case 'ambiguous':
                publish({ kind: 'ambiguous' });
                return;
            default: {
                const exhaustiveResult: never = result;
                return exhaustiveResult;
            }
        }
    }

    return {
        getSnapshot(): InterruptUiState {
            return state;
        },
        subscribe(listener: () => void): () => void {
            listeners.add(listener);
            return function unsubscribe(): void {
                listeners.delete(listener);
            };
        },
        requestStop(): void {
            if (
                state.kind === 'pending' ||
                state.kind === 'already_inactive' ||
                state.kind === 'authoritatively_resolved' ||
                options.interrupt === null
            ) {
                return;
            }
            requestGeneration += 1;
            const generation = requestGeneration;
            const startedAtForegroundMs = options.foregroundClock.elapsedMs();
            publish({
                kind: 'pending',
                startedAtForegroundMs,
            });
            void options
                .interrupt()
                .then((result) =>
                    publishRequestResult(
                        generation,
                        startedAtForegroundMs,
                        result,
                    ),
                )
                .catch((error: unknown) => {
                    // The transport releases its ordering slot on timeout,
                    // but presentation still waits for foreground catch-up.
                    if (
                        error instanceof DOMException &&
                        error.name === 'TimeoutError'
                    ) {
                        return;
                    }
                    if (
                        generation === requestGeneration &&
                        state.kind !== 'authoritatively_resolved'
                    ) {
                        publish({ kind: 'ambiguous' });
                    }
                });
        },
        evaluateDeadline(authoritativeCatchUpComplete: boolean): void {
            if (
                (state.kind !== 'pending' && state.kind !== 'accepted') ||
                !authoritativeCatchUpComplete
            ) {
                return;
            }
            const elapsedMs =
                options.foregroundClock.elapsedMs() -
                state.startedAtForegroundMs;
            if (elapsedMs >= INTERRUPT_DEADLINE_MS) {
                options.onDeadline?.();
                publish({ kind: 'ambiguous' });
            }
        },
        resolveAuthoritatively(): void {
            if (
                state.kind === 'idle' ||
                state.kind === 'authoritatively_resolved'
            ) {
                return;
            }
            publish({ kind: 'authoritatively_resolved' });
        },
        reset(): void {
            // A resolved run that shows new work is a new response; it must
            // be stoppable again, and any late result belongs to the old one.
            if (
                state.kind !== 'authoritatively_resolved' &&
                state.kind !== 'already_inactive'
            ) {
                return;
            }
            requestGeneration += 1;
            publish({ kind: 'idle' });
        },
        deadlineRemainingMs(): number | null {
            if (state.kind !== 'pending' && state.kind !== 'accepted') {
                return null;
            }
            return Math.max(
                0,
                INTERRUPT_DEADLINE_MS -
                    (options.foregroundClock.elapsedMs() -
                        state.startedAtForegroundMs),
            );
        },
    };
}

function renderEmptyState(
    _scope: HarnessScope,
    surface: V3PhoebeSurface,
): ReactNode {
    return createElement(ChatHomeStage, {
        greeting: getV3PhoebePresentation(surface).copy.emptyTitle,
    });
}

export function v3PhoebeAgentProfile(
    ports: AgentProfilePorts,
    surface: V3PhoebeSurface = 'general',
): AgentProfile {
    const presentation = getV3PhoebePresentation(surface);
    return {
        id: presentation.profileId,
        rendererVersion: V3_PHOEBE_RENDERER_VERSION,
        displayName: 'Phoebe',
        rendererOverlay: V3_PHOEBE_RENDERER_OVERLAY,
        copy: presentation.copy,
        transport: ports.transport,
        features: {
            composer: {
                kind: 'text',
                placeholder: presentation.placeholder,
                send: ports.writes.sendMessage,
                presets: presentation.presets,
            },
            questions: {
                kind: 'supported',
                answer: ports.writes.answerQuestion,
            },
            toolApprovals: {
                kind: 'supported',
                decide: ports.writes.decideToolApproval,
            },
            interrupt: { kind: 'supported', interrupt: ports.writes.interrupt },
        },
        emptyState: (scope) => renderEmptyState(scope, surface),
    };
}

export function v3AgentProfileResolver(
    profile: AgentProfile,
): AgentProfileResolver {
    return {
        id: profile.id,
        resolve(summary) {
            if (summary.is_agent_v3 === true) {
                return {
                    kind: 'matched',
                    summary,
                    profile,
                };
            }
            return {
                kind: 'different_experience',
                summary,
            };
        },
    };
}
